// Small DOM helpers shared by the Translate and Room apps.

export function escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/** Parse an HTML string into a single element. */
export function fromHtml(html) {
    const tpl = document.createElement('template');
    tpl.innerHTML = html.trim();
    return tpl.content.firstElementChild;
}

export function $(selector, root = document) {
    return root.querySelector(selector);
}

let toastEl = null;
let toastTimer = null;

/** Brief status message at the bottom of the screen. */
export function toast(message, ms = 2600, { action = null } = {}) {
    if (!toastEl) {
        toastEl = document.createElement('div');
        toastEl.className = 'pg-toast';
        toastEl.setAttribute('role', 'status');
        toastEl.setAttribute('aria-live', 'polite');
        document.body.appendChild(toastEl);
    }
    toastEl.replaceChildren(document.createTextNode(message));
    // Optional action (e.g. Undo). The toast stays longer so there's time to tap it.
    if (action) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'pg-toast__action';
        button.textContent = action.label;
        button.addEventListener('click', () => {
            toastEl.classList.remove('is-visible');
            action.run();
        });
        toastEl.appendChild(button);
        ms = Math.max(ms, 6000);
    }
    toastEl.classList.toggle('pg-toast--action', !!action);
    toastEl.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toastEl.classList.remove('is-visible'), ms);
}

/**
 * Wire a button to show/hide a popover menu. Closes on outside click and Escape.
 * Returns a close() function.
 */
export function bindMenu(button, menu) {
    const close = () => {
        menu.hidden = true;
        button.setAttribute('aria-expanded', 'false');
    };
    const open = () => {
        menu.hidden = false;
        button.setAttribute('aria-expanded', 'true');
        menu.querySelector('button, a')?.focus();
    };
    button.setAttribute('aria-haspopup', 'true');
    button.setAttribute('aria-expanded', 'false');
    button.addEventListener('click', (e) => {
        e.stopPropagation();
        if (menu.hidden) open(); else close();
    });
    document.addEventListener('click', (e) => {
        if (!menu.hidden && !menu.contains(e.target)) close();
    });
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !menu.hidden) {
            close();
            button.focus();
        }
    });
    menu.addEventListener('click', (e) => {
        if (e.target.closest('.pg-menu__item')) close();
    });
    return close;
}

export async function copyText(text) {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        // Fallback for browsers without async clipboard permission
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        let ok = false;
        try { ok = document.execCommand('copy'); } catch { ok = false; }
        ta.remove();
        return ok;
    }
}

export function formatTime(date) {
    return new Date(date).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}
